import React, { useState, useEffect } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import SearchInput from "@/components/SearchInput/SearchInput";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface FilterMetaItem {
  id: number;
  name: string;
}

interface ResourceFiltersProps {
  filters: {
    searchTerm: string;
    tradeIds: string[];
    resourceTypeIds: string[];
    businessRoleIds: string[];
    hasMigrationErrors?: boolean;
  };
  onFilterChange: (filterKey: string, value: string | string[] | boolean) => void;
  resourceTypes: FilterMetaItem[];
  trades: FilterMetaItem[];
  businessRoles: FilterMetaItem[];
}

const ResourceFilters: React.FC<ResourceFiltersProps> = ({
  filters,
  onFilterChange,
  resourceTypes,
  trades,
  businessRoles,
}) => {
  const [searchInput, setSearchInput] = useState(filters.searchTerm);
  const debouncedSearch = useDebouncedValue(searchInput, 500);

  useEffect(() => {
    if (debouncedSearch !== filters.searchTerm) {
      onFilterChange("searchTerm", debouncedSearch);
    }
  }, [debouncedSearch, filters.searchTerm, onFilterChange]);

  const resourceTypeOptions = resourceTypes.map((rt) => ({
    label: rt.name,
    value: String(rt.id),
  }));
  const tradeOptions = trades.map((t) => ({
    label: t.name,
    value: String(t.id),
  }));
  const roleOptions = businessRoles.map((br) => ({
    label: br.name,
    value: String(br.id),
  }));

  return (
    <div className="mb-4 space-y-4">
      <SearchInput
        onChange={(val) => setSearchInput(val)}
        placeholder="Search by title..."
        value={searchInput}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <MultiSelect
          onChange={(newVals) => onFilterChange("resourceTypeIds", newVals)}
          options={resourceTypeOptions}
          placeholder="Select resource types..."
          value={filters.resourceTypeIds}
        />

        <MultiSelect
          onChange={(newVals) => onFilterChange("tradeIds", newVals)}
          options={tradeOptions}
          placeholder="Select trades..."
          value={filters.tradeIds}
        />

        <MultiSelect
          onChange={(newVals) => onFilterChange("businessRoleIds", newVals)}
          options={roleOptions}
          placeholder="Select roles..."
          value={filters.businessRoleIds}
        />
      </div>

      <div className="flex items-center space-x-2 rounded-lg border p-3">
        <Switch
          id="migration-errors"
          checked={filters.hasMigrationErrors || false}
          onCheckedChange={(checked) => onFilterChange("hasMigrationErrors", checked)}
        />
        <Label htmlFor="migration-errors" className="cursor-pointer">
          Show resources with migration errors
        </Label>
      </div>
    </div>
  );
};

export default ResourceFilters;
